import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileCheck2, BellRing } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;

export const QuoteAutomationSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [{ data }, { data: p }] = await Promise.all([axios.get(`${API}/notifications/quote-automation`), axios.get(`${API}/notifications/quote-follow-up-preview`)]);
    setCfg(data); setForm({ auto_invoice_enabled: data.auto_invoice_enabled, follow_up_enabled: data.follow_up_enabled, follow_up_days_before: data.follow_up_days_before, hour: data.hour }); setPreview(p);
  };
  useEffect(() => { load().catch(() => {}); }, []);
  if (!form) return null;

  const save = async () => {
    setBusy("save");
    try {
      await axios.put(`${API}/notifications/quote-automation`, { ...form, follow_up_days_before: Number(form.follow_up_days_before) });
      toast.success("Quote automation saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(null);
    }
  };
  const sendNow = async () => {
    if (!window.confirm(`Send quote follow-up emails now for ${preview?.count || 0} quote(s)?`)) return;
    setBusy("send");
    try {
      const { data } = await axios.post(`${API}/notifications/send-quote-follow-ups`);
      if (data.reminders_sent === 0 && data.errors.length === 0) toast.info("No quotes need a follow-up right now");
      else if (data.errors.length) toast.warning(`Sent ${data.reminders_sent}; ${data.errors.length} error(s): ${data.errors[0]}`);
      else toast.success(`Sent ${data.reminders_sent} quote follow-up email(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send follow-ups");
    } finally {
      setBusy(null);
    }
  };

  const dirty = cfg && ["auto_invoice_enabled", "follow_up_enabled", "hour"].some((k) => cfg[k] !== form[k]) || Number(form.follow_up_days_before) !== cfg?.follow_up_days_before;
  const last = cfg?.last_result;
  return (
    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4" data-testid="quote-automation-settings">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <FileCheck2 className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <p className="font-semibold text-purple-900">Auto-invoice on customer approval</p>
            <p className="text-sm text-purple-800">When a customer approves a quote from the email link, the invoice is created and emailed to them automatically (with the View &amp; Pay link).</p>
          </div>
        </div>
        <Switch checked={form.auto_invoice_enabled} onCheckedChange={(v) => setForm({ ...form, auto_invoice_enabled: v })} data-testid="quote-auto-invoice-toggle" />
      </div>
      <div className="border-t border-purple-200 pt-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <BellRing className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <p className="font-semibold text-purple-900">Quote expiry follow-up</p>
            <p className="text-sm text-purple-800">Customers who haven't responded get one friendly reminder (with the review link and PDF) shortly before the quote expires.</p>
          </div>
        </div>
        <Switch checked={form.follow_up_enabled} onCheckedChange={(v) => setForm({ ...form, follow_up_enabled: v })} data-testid="quote-follow-up-toggle" />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-36">
          <Label className="text-purple-900">Days before expiry</Label>
          <Input type="number" min="1" max="30" value={form.follow_up_days_before} onChange={(e) => setForm({ ...form, follow_up_days_before: e.target.value })} className="bg-white" data-testid="quote-follow-up-days-input" />
        </div>
        <div className="w-44">
          <Label className="text-purple-900">Check time (Eastern)</Label>
          <Select value={String(form.hour)} onValueChange={(v) => setForm({ ...form, hour: Number(v) })}>
            <SelectTrigger className="bg-white" data-testid="quote-follow-up-hour-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={save} disabled={!dirty || !!busy} size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="quote-automation-save-button">{busy === "save" ? "Saving…" : "Save"}</Button>
        <Button onClick={sendNow} disabled={!!busy || !preview?.count} size="sm" variant="outline" className="bg-white" data-testid="quote-follow-up-send-now-button">
          <BellRing className="w-4 h-4 mr-2" />{busy === "send" ? "Sending…" : `Send Follow-ups Now${preview ? ` (${preview.count})` : ""}`}
        </Button>
      </div>
      {preview && preview.count > 0 && (
        <p className="text-xs text-purple-900" data-testid="quote-follow-up-preview">
          Waiting for a follow-up: {preview.quotes.slice(0, 4).map((q) => `${q.quote_number} (${q.customer_name}, expires ${q.valid_until})`).join(", ")}{preview.count > 4 ? ` +${preview.count - 4} more` : ""}
        </p>
      )}
      <p className="text-xs text-purple-800" data-testid="quote-follow-up-last-run">
        Last follow-up run: {cfg?.last_run ? new Date(cfg.last_run).toLocaleString() : "never"}{last ? ` · ${last.reminders_sent} sent, ${last.errors?.length || 0} error(s) (${last.trigger})` : ""}
      </p>
    </div>
  );
};
